import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Workflow, Sparkles, Eye, Trash2, Lock, ExternalLink, Loader2, CheckCircle2,
  X, RefreshCw, AlertTriangle, Send, FileCode2, ArchiveRestore,
} from 'lucide-react';
import HelpTip from '@/components/HelpTip';

const selectCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white';
const areaCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono min-h-[140px]';

const FLOW_CATS = [
  { id: 'LEAD_GENERATION', label: 'Captar leads' },
  { id: 'APPOINTMENT_BOOKING', label: 'Agendar cita' },
  { id: 'SIGN_UP', label: 'Registro' },
  { id: 'CONTACT_US', label: 'Contacto' },
  { id: 'SURVEY', label: 'Encuesta' },
  { id: 'OTHER', label: 'Otro' },
];

function FlowStatus({ status }) {
  const s = (status || '').toUpperCase();
  if (s === 'PUBLISHED') return <Badge className="bg-emerald-500/20 text-emerald-300 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />Publicado</Badge>;
  if (s === 'DRAFT') return <Badge className="bg-amber-500/20 text-amber-300 border-0">Borrador</Badge>;
  if (s === 'DEPRECATED') return <Badge className="bg-zinc-700 text-zinc-400 border-0">Retirado</Badge>;
  return <Badge className="bg-zinc-700 text-zinc-300 border-0">{s || '—'}</Badge>;
}

export default function GupshupFlowsPanel({ appId, selectedApp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [jsonView, setJsonView] = useState(null);   // {name, json}

  // wizard
  const [wiz, setWiz] = useState(false);
  const [step, setStep] = useState('describe');     // describe | review | created
  const [brief, setBrief] = useState('');
  const [language, setLanguage] = useState('es');
  const [aiBusy, setAiBusy] = useState(false);
  const [flowJson, setFlowJson] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [name, setName] = useState('');
  const [categories, setCategories] = useState('LEAD_GENERATION');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);      // {flow_id, preview_url, validation_errors}
  const [previewed, setPreviewed] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    if (!appId) return;
    setLoading(true); setData(null);
    try {
      const r = await api.get('/admin/platform/gupshup/flows', { params: { app_id: appId } });
      setData(r.data);
    } catch (e) {
      setData({ flows: [] });
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los flows');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);

  const flows = data?.flows || [];

  const openPreview = async (f) => {
    setBusy(`prev-${f.id}`);
    try {
      const { data: d } = await api.get(`/admin/platform/gupshup/flows/${appId}/${f.id}/preview`);
      if (d.preview_url) window.open(d.preview_url, '_blank', 'noopener');
      else toast.error('Sin vista previa disponible');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo abrir el preview'); }
    finally { setBusy(''); }
  };

  const viewJson = async (f) => {
    setBusy(`json-${f.id}`);
    try {
      const { data: d } = await api.get(`/admin/platform/gupshup/flows/${appId}/${f.id}/json`);
      setJsonView({ name: f.name, json: d.flow_json || '(sin JSON)' });
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo leer el JSON'); }
    finally { setBusy(''); }
  };

  const publish = async (f) => {
    if (!window.confirm(`Publicar el flow "${f.name}". Asegúrate de haber revisado su vista previa. ¿Continuar?`)) return;
    setBusy(`pub-${f.id}`);
    try { await api.post(`/admin/platform/gupshup/flows/${appId}/${f.id}/publish`); toast.success('Flow publicado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo publicar'); }
    finally { setBusy(''); }
  };
  const deprecate = async (f) => {
    if (!window.confirm(`Retirar (depreciar) el flow "${f.name}". Dejará de poder usarse. ¿Continuar?`)) return;
    setBusy(`dep-${f.id}`);
    try { await api.post(`/admin/platform/gupshup/flows/${appId}/${f.id}/deprecate`); toast.success('Flow retirado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo retirar'); }
    finally { setBusy(''); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Borrar el flow "${f.name}". Es irreversible. ¿Continuar?`)) return;
    setBusy(`del-${f.id}`);
    try { await api.delete(`/admin/platform/gupshup/flows/${appId}/${f.id}`); toast.success('Flow borrado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo borrar'); }
    finally { setBusy(''); }
  };

  const openWizard = () => {
    setStep('describe'); setBrief(''); setFlowJson(''); setWarnings([]); setName('');
    setCategories('LEAD_GENERATION'); setCreated(null); setPreviewed(false); setWiz(true);
  };

  const generar = async () => {
    if (!brief.trim()) { toast.error('Describe el formulario que quieres'); return; }
    setAiBusy(true);
    try {
      const { data: d } = await api.post('/admin/platform/gupshup/flows/ai', { app_id: appId, brief: brief.trim(), language });
      setFlowJson(d.flow_json || ''); setWarnings(d.warnings || []); setName(d.name || 'Formulario');
      setStep('review');
    } catch (e) { toast.error(e.response?.data?.detail || 'La IA no pudo generar el flow'); }
    finally { setAiBusy(false); }
  };

  const crearBorrador = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre al flow'); return; }
    if (!flowJson.trim()) { toast.error('Falta el contenido del flow'); return; }
    setCreating(true);
    try {
      const { data: d } = await api.post('/admin/platform/gupshup/flows', {
        app_id: appId, name: name.trim(), categories: [categories], flow_json: flowJson,
      });
      setCreated(d); setPreviewed(false); setStep('created');
      if ((d.validation_errors || []).length) toast.warning('El flow se creó pero Meta reportó observaciones; revísalas.');
      else toast.success('Borrador creado. Revisa la vista previa antes de publicar.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo crear el borrador'); }
    finally { setCreating(false); }
  };

  const publishWizard = async () => {
    if (!created?.flow_id) return;
    setPublishing(true);
    try {
      await api.post(`/admin/platform/gupshup/flows/${appId}/${created.flow_id}/publish`);
      toast.success('¡Flow publicado!'); setWiz(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo publicar'); }
    finally { setPublishing(false); }
  };

  const descartarBorrador = async () => {
    if (created?.flow_id) {
      try { await api.delete(`/admin/platform/gupshup/flows/${appId}/${created.flow_id}`); } catch { /* noop */ }
    }
    setWiz(false); load();
  };

  const counts = useMemo(() => ({
    published: flows.filter((f) => f.status === 'PUBLISHED').length,
    draft: flows.filter((f) => f.status === 'DRAFT').length,
  }), [flows]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-3">
        <div className="flex gap-2">
          <Button onClick={openWizard} className="bg-emerald-600 hover:bg-emerald-500 gap-1" disabled={!appId}>
            <Sparkles className="w-4 h-4" /> Crear flow con IA
          </Button>
          <Button onClick={load} variant="ghost" className="text-zinc-400" disabled={!appId} title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="text-xs text-zinc-500 sm:ml-auto">
          {data ? `${counts.published} publicados · ${counts.draft} borradores` : ''}
        </div>
      </div>

      <div className="rounded-lg bg-sky-950/20 border border-sky-500/20 p-2.5 text-[11px] text-sky-200 mb-3 flex items-start gap-2">
        <Workflow className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Los <b>Flows</b> son formularios interactivos dentro de WhatsApp (pedir datos, agendar, calificar). Aquí los creas y publicas; el envío a los leads lo maneja el sistema. Los marcados con <Lock className="w-3 h-3 inline" /> son del sistema: solo lectura.</span>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando flows…</div>
      ) : !appId ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Elige un número para ver sus flows.</div>
      ) : flows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center border border-dashed border-white/10 rounded-lg">Este número aún no tiene flows. Crea el primero con IA.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {flows.map((f) => (
            <Card key={f.id} className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-4 flex flex-col gap-2 h-full">
                <div className="flex flex-wrap items-center gap-1.5">
                  <FlowStatus status={f.status} />
                  {f.protected && <Badge className="bg-indigo-500/15 text-indigo-300 border-0 gap-1"><Lock className="w-3 h-3" />Del sistema</Badge>}
                  {Array.isArray(f.validation_errors) && f.validation_errors.length > 0 &&
                    <Badge className="bg-red-500/15 text-red-300 border-0 gap-1"><AlertTriangle className="w-3 h-3" />{f.validation_errors.length}</Badge>}
                </div>
                <div className="text-sm font-semibold text-white break-words">{f.name}</div>
                <div className="text-[11px] text-zinc-500">{(f.categories || []).join(', ')}</div>
                <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                  <Button size="sm" variant="ghost" className="text-zinc-300 h-8 px-2" onClick={() => openPreview(f)} disabled={busy === `prev-${f.id}`}>
                    {busy === `prev-${f.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1" />Preview</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300 h-8 px-2" onClick={() => viewJson(f)} disabled={busy === `json-${f.id}`}>
                    <FileCode2 className="w-4 h-4 mr-1" />JSON
                  </Button>
                  {!f.protected && f.status === 'DRAFT' && (
                    <Button size="sm" variant="ghost" className="text-emerald-300 h-8 px-2" onClick={() => publish(f)} disabled={busy === `pub-${f.id}`}>
                      {busy === `pub-${f.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Publicar</>}
                    </Button>
                  )}
                  {!f.protected && f.status === 'PUBLISHED' && (
                    <Button size="sm" variant="ghost" className="text-amber-300 h-8 px-2" onClick={() => deprecate(f)} disabled={busy === `dep-${f.id}`}>
                      <ArchiveRestore className="w-4 h-4 mr-1" />Retirar
                    </Button>
                  )}
                  {!f.protected && f.status !== 'PUBLISHED' && (
                    <Button size="sm" variant="ghost" className="text-red-300 h-8 px-2 ml-auto" onClick={() => remove(f)} disabled={busy === `del-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Ver JSON */}
      {jsonView && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setJsonView(null)}>
          <div className="bg-zinc-950 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 sticky top-0 bg-zinc-950">
              <FileCode2 className="w-5 h-5 text-emerald-400" /><span className="text-white font-semibold text-sm truncate">{jsonView.name}</span>
              <Button variant="ghost" className="ml-auto text-zinc-400 h-8 w-8 p-0" onClick={() => setJsonView(null)}><X className="w-5 h-5" /></Button>
            </div>
            <pre className="p-4 text-[11px] text-zinc-300 whitespace-pre-wrap break-words">{jsonView.json}</pre>
          </div>
        </div>
      )}

      {/* Wizard crear con IA */}
      {wiz && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => !creating && !publishing && !aiBusy && setWiz(false)}>
          <div className="bg-zinc-950 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-950 border-b border-white/10 px-4 py-3 flex items-center gap-2 z-10">
              <Workflow className="w-5 h-5 text-emerald-400" />
              <h3 className="text-white font-semibold">Crear flow con IA</h3>
              <span className="text-xs text-zinc-500 truncate ml-1">· {selectedApp?.name}</span>
              <Button variant="ghost" className="ml-auto text-zinc-400 h-8 w-8 p-0" onClick={() => !creating && !publishing && setWiz(false)}><X className="w-5 h-5" /></Button>
            </div>

            <div className="p-4 space-y-4">
              {/* pasos */}
              <div className="flex items-center gap-2 text-[11px]">
                {['Describe', 'Revisa', 'Publica'].map((lbl, i) => {
                  const active = (step === 'describe' && i === 0) || (step === 'review' && i === 1) || (step === 'created' && i === 2);
                  const done = (step === 'review' && i === 0) || (step === 'created' && i < 2);
                  return (
                    <div key={lbl} className={`flex items-center gap-1 ${active ? 'text-emerald-300' : done ? 'text-emerald-500/60' : 'text-zinc-600'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center border ${active ? 'border-emerald-400' : 'border-zinc-700'}`}>{i + 1}</span>{lbl}
                      {i < 2 && <span className="w-4 h-px bg-zinc-700 mx-1" />}
                    </div>
                  );
                })}
              </div>

              {step === 'describe' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-zinc-400 flex items-center gap-1">Describe el formulario
                      <HelpTip title="¿Qué es un flow?" content="Un formulario dentro de WhatsApp. Describe qué datos quieres pedir y la IA lo arma. Lo más confiable es un solo paso con todos los campos." />
                    </Label>
                    <textarea className={areaCls.replace('font-mono ', '')} value={brief} onChange={(e) => setBrief(e.target.value)}
                      placeholder="Ej: Formulario para captar un lead: nombre, teléfono, zona de interés (norte/centro/sur) y forma de contacto (llamada o WhatsApp), con un botón Enviar." />
                  </div>
                  <div className="w-32">
                    <Label className="text-xs text-zinc-400">Idioma</Label>
                    <select className={selectCls} value={language} onChange={(e) => setLanguage(e.target.value)}>
                      <option value="es">Español</option><option value="en">Inglés</option><option value="pt">Portugués</option>
                    </select>
                  </div>
                  <Button onClick={generar} disabled={aiBusy} className="bg-emerald-600 hover:bg-emerald-500 gap-1">
                    {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar con IA
                  </Button>
                </div>
              )}

              {step === 'review' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400">Nombre del flow</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Tipo</Label>
                      <select className={selectCls} value={categories} onChange={(e) => setCategories(e.target.value)}>
                        {FLOW_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                  {warnings.length > 0 && (
                    <div className="rounded-lg bg-amber-950/20 border border-amber-500/20 p-2.5 text-[11px] text-amber-200">
                      <div className="flex items-center gap-1 font-semibold mb-1"><AlertTriangle className="w-3.5 h-3.5" />Avisos</div>
                      <ul className="list-disc list-inside space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-zinc-400 flex items-center gap-1">Contenido del flow (editable)
                      <HelpTip title="¿Puedo editarlo?" content="Es el JSON del flow. Puedes ajustarlo si sabes cómo, o dejarlo tal cual. Al crear el borrador se valida contra Meta y verás la vista previa antes de publicar." />
                    </Label>
                    <textarea className={areaCls} value={flowJson} onChange={(e) => setFlowJson(e.target.value)} spellCheck={false} />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="border-white/10" onClick={() => setStep('describe')}>Atrás</Button>
                    <Button onClick={crearBorrador} disabled={creating} className="bg-emerald-600 hover:bg-emerald-500 gap-1">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Crear borrador y previsualizar
                    </Button>
                  </div>
                </div>
              )}

              {step === 'created' && created && (
                <div className="space-y-3">
                  <div className="rounded-lg bg-emerald-950/20 border border-emerald-500/20 p-3 text-sm text-emerald-200 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Borrador creado. Revisa cómo se ve antes de publicarlo.
                  </div>
                  {(created.validation_errors || []).length > 0 && (
                    <div className="rounded-lg bg-red-950/20 border border-red-500/20 p-2.5 text-[11px] text-red-200">
                      <div className="flex items-center gap-1 font-semibold mb-1"><AlertTriangle className="w-3.5 h-3.5" />Meta reportó observaciones</div>
                      <ul className="list-disc list-inside space-y-0.5">{created.validation_errors.slice(0, 6).map((e, i) => <li key={i}>{e.message || JSON.stringify(e).slice(0, 80)}</li>)}</ul>
                      <p className="mt-1 text-red-300/80">Si la vista previa no se ve bien, no lo publiques: vuelve atrás y regenera.</p>
                    </div>
                  )}
                  <a href={created.preview_url || '#'} target="_blank" rel="noopener noreferrer"
                    onClick={() => setPreviewed(true)}
                    className={`inline-flex items-center gap-1 text-sm underline ${created.preview_url ? 'text-emerald-300' : 'text-zinc-600 pointer-events-none'}`}>
                    <ExternalLink className="w-4 h-4" /> Abrir vista previa
                  </a>
                  <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={previewed} onChange={(e) => setPreviewed(e.target.checked)} className="mt-0.5" />
                    Revisé la vista previa y el formulario se ve y funciona bien.
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="border-white/10 text-zinc-300" onClick={descartarBorrador}>Descartar borrador</Button>
                    <Button onClick={publishWizard} disabled={!previewed || publishing} className="bg-emerald-600 hover:bg-emerald-500 gap-1">
                      {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publicar flow
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-500">Publicar solo se habilita cuando confirmas que revisaste la vista previa.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
